import React, { useEffect, useState } from 'react';
import { Smartphone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PromptCard from '../ui/PromptCard';
import { supabase } from '../../lib/supabase';

const STORAGE_KEY = 'ethernal_backfill_banner_dismissed';

/**
 * One-time soft banner for users who created stories before V1 of the
 * notifications system. The migration that added user_notification_preferences
 * backfilled their rows with `paused_until = now() + 7 days` so reminders
 * don't surprise them. This banner offers them the chance to upgrade to
 * push notifications by jumping into /activate-push.
 *
 * Trigger: at least one paused preferences row + no push subscriptions +
 * not dismissed via localStorage. Once dismissed, never shown again to
 * this user/browser combination.
 */
const NotificationBackfillBanner: React.FC = () => {
    const navigate = useNavigate();
    const [show, setShow] = useState(false);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                if (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) {
                    return;
                }

                const {
                    data: { user },
                } = await supabase.auth.getUser();
                if (!user) return;

                // Has at least one paused preferences row (the backfill grace).
                const { count: pausedCount } = await supabase
                    .from('user_notification_preferences')
                    .select('id', { count: 'exact', head: true })
                    .eq('user_id', user.id)
                    .gt('paused_until', new Date().toISOString());

                if (!pausedCount || pausedCount < 1) return;

                // Already has at least one push subscription? No banner needed.
                const { count: subCount } = await supabase
                    .from('push_subscriptions')
                    .select('id', { count: 'exact', head: true })
                    .eq('user_id', user.id);

                if (subCount && subCount > 0) return;

                if (!cancelled) setShow(true);
            } catch (err) {
                console.warn('[NotificationBackfillBanner] check failed:', err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleDismiss = () => {
        try {
            localStorage.setItem(STORAGE_KEY, 'true');
        } catch {
            // localStorage unavailable — ignore.
        }
        setShow(false);
    };

    if (!show) return null;

    return (
        <PromptCard
            icon={Smartphone}
            headline="Activa los avisos del móvil"
            body="Recibe los recordatorios al instante en lugar de por correo."
            cta={{
                label: 'Configurar',
                onClick: () => {
                    handleDismiss();
                    navigate('/activate-push');
                },
            }}
            onDismiss={handleDismiss}
        />
    );
};

export default NotificationBackfillBanner;
